import type { FlattenedSectionProps } from "../../../components/Questionnaire/Section";
import type { QuestionProps } from "../../../components/Questionnaire/Section/Question/types";
import type { Answers, QuestionnaireState } from "../types";

export const hasUnansweredRequiredQuestions = (
  questions: QuestionProps[],
  answers: Answers,
) => questions.some(q => isRequired(q) && !answers[q.name]);

const isRequired = (q: QuestionProps) => q.visible && q.required;

const isVisible = (question: QuestionProps) => question.visible ?? false;

export const getVisibleSection = (
  state: QuestionnaireState,
  direction: "next" | "previous",
): number => {
  const { sections: sectionsFromState, answers, currentSectionIndex } = state;
  const sections = sectionsFromState as unknown as FlattenedSectionProps[];

  if (direction === "next") {
    // Convert to an array of booleans indicating whether the section is visible
    const sectionVisibility = sections.map((section, index) => {
      if (index === currentSectionIndex) {
        return hasUnansweredRequiredQuestions(section.questions, answers);
      }

      return section.questions.some(isVisible);
    });

    const nextIndex = sectionVisibility.indexOf(true, currentSectionIndex);
    // If no sections are required, return the final section index
    if (nextIndex === -1) {
      return sections.length - 1;
    }

    // If there are no more sections to visit, return the current section index
    return nextIndex < currentSectionIndex ? currentSectionIndex : nextIndex;
  }

  // Convert to an array of booleans indicating whether the section is visible
  const sectionVisibility = sections.map(({ questions }) =>
    questions.some(isVisible),
  );

  const prevIndex = sectionVisibility.lastIndexOf(
    true,
    currentSectionIndex - 1,
  );

  if (prevIndex === -1) {
    return currentSectionIndex;
  }

  return prevIndex > currentSectionIndex ? currentSectionIndex : prevIndex;
};

export const getNextVisibleSection = (state: QuestionnaireState): number =>
  getVisibleSection(state, "next");

export const getPreviousVisibleSection = (state: QuestionnaireState): number =>
  getVisibleSection(state, "previous");
